import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { trackPageview } from "@/utils/analytics";
import { initAiAttribution } from "@/utils/aiAttribution";

// Pages
import HomePage from "@/pages/HomePage";
import AboutUsPage from "@/pages/AboutUsPage";
import ShopFlooringPage from "@/pages/ShopFlooringPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ProductComparisonPage from "@/pages/ProductComparisonPage";
import GuidedSelectorPage from "@/pages/GuidedSelectorPage";
import SolutionsPage from "@/pages/SolutionsPage";
import ResourcesPage from "@/pages/ResourcesPage";
import ContactUsPage from "@/pages/ContactUsPage";
import AccountPage from "@/pages/AccountPage";
import ConciergePage from "@/pages/ConciergePage";
import MaintenanceProgramPage from "@/pages/MaintenanceProgramPage";
import CustomHubPage from "@/pages/CustomHubPage";
import CustomLogoMatsPage from "@/pages/CustomLogoMatsPage";
import CustomFlooringSolutionsPage from "@/pages/CustomFlooringSolutionsPage";
import CustomInstallationsPage from "@/pages/CustomInstallationsPage";
import CustomConsultationPage from "@/pages/CustomConsultationPage";
import CustomPortfolioPage from "@/pages/CustomPortfolioPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import CareersPage from "@/pages/CareersPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import NotFoundPage from "@/pages/NotFoundPage";
import CatalogueViewerPage from "@/pages/CatalogueViewerPage";
import ArticlePage from "@/pages/ArticlePage";
import { LegacyRedirectsGate } from "@/components/shared/LegacyRedirectsGate";

// Dashboard
import { DashboardAuthProvider } from "@/dashboard/AuthContext";
import { ProtectedRoute } from "@/dashboard/ProtectedRoute";
import { DashboardLayout } from "@/dashboard/DashboardLayout";
import DashboardLoginPage from "@/dashboard/pages/LoginPage";
import DashboardForgotPasswordPage from "@/dashboard/pages/ForgotPasswordPage";
import DashboardResetPasswordPage from "@/dashboard/pages/ResetPasswordPage";
import DashboardOverviewPage from "@/dashboard/pages/OverviewPage";
import DashboardSubmissionsListPage from "@/dashboard/pages/SubmissionsListPage";
import DashboardFailedSubmissionsPage from "@/dashboard/pages/FailedSubmissionsPage";
import DashboardSubmissionDetailPage from "@/dashboard/pages/SubmissionDetailPage";
import DashboardCompaniesPage from "@/dashboard/pages/CompaniesPage";
import DashboardCompanyDetailPage from "@/dashboard/pages/CompanyDetailPage";
import DashboardPipelinePage from "@/dashboard/pages/PipelinePage";
import DashboardUsersPage from "@/dashboard/pages/UsersPage";
import DashboardAccountPage from "@/dashboard/pages/AccountPage";
import LeadActionPage from "@/pages/LeadActionPage";

// Global Components
import CookieConsentBanner from "@/components/shared/CookieConsentBanner";

// Scroll to top and force refresh on route change
function ScrollToTop() {
  const location = useLocation();
  const prevPathname = React.useRef(location.pathname);

  useEffect(() => {
    const isSamePage = prevPathname.current === location.pathname;
    prevPathname.current = location.pathname;

    // Fire a GA4 pageview on every route change (no-op until consent + initGA)
    trackPageview(location.pathname + location.search);

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const scrollToElement = (attempt) => {
        const el = document.getElementById(id);
        if (el) {
          const headerOffset = 130;
          const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: isSamePage ? 'smooth' : 'instant' });
        } else if (attempt < 3) {
          setTimeout(() => scrollToElement(attempt + 1), 400);
        }
      };
      // Longer delay for cross-page to ensure DOM is ready
      setTimeout(() => scrollToElement(1), isSamePage ? 50 : 300);
    } else {
      // Force scroll to top reliably across browsers and any animated route transitions
      const scrollTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      };
      scrollTop();
      // Retry on next frame in case route transition or layout shift moved scroll
      requestAnimationFrame(scrollTop);
      setTimeout(scrollTop, 50);
    }
  }, [location.pathname, location.search, location.key, location.hash]);

  return null;
}

function App() {
  useEffect(() => {
    // Detect AI-search referrals (Perplexity / ChatGPT / Claude / Copilot / ...)
    // and fire a one-time visit beacon so we can attribute leads later.
    initAiAttribution();
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <CookieConsentBanner />
        <DashboardAuthProvider>
          <Routes>
            {/* Dashboard (must come BEFORE catch-all 404) */}
            <Route path="/dashboard/login" element={<DashboardLoginPage />} />
            <Route path="/dashboard/forgot-password" element={<DashboardForgotPasswordPage />} />
            <Route path="/dashboard/reset-password" element={<DashboardResetPasswordPage />} />
            {/* Public magic-link rep status updater */}
            <Route path="/lead-action/:token" element={<LeadActionPage />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<DashboardOverviewPage />} />
                <Route path="/dashboard/submissions" element={<DashboardSubmissionsListPage />} />
                <Route path="/dashboard/submissions/:id" element={<DashboardSubmissionDetailPage />} />
                <Route path="/dashboard/failed-submissions" element={<DashboardFailedSubmissionsPage />} />
                <Route path="/dashboard/companies" element={<DashboardCompaniesPage />} />
                <Route path="/dashboard/companies/:key" element={<DashboardCompanyDetailPage />} />
                <Route path="/dashboard/pipeline" element={<DashboardPipelinePage />} />
                <Route path="/dashboard/account" element={<DashboardAccountPage />} />
              </Route>
              <Route element={<ProtectedRoute adminOnly />}>
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard/users" element={<DashboardUsersPage />} />
                </Route>
              </Route>
            </Route>

            {/* Main Pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/products" element={<ShopFlooringPage />} />
            <Route path="/products/:slug" element={<ProductDetailPage />} />
            <Route path="/catalogue" element={<CatalogueViewerPage />} />
            <Route path="/compare" element={<ProductComparisonPage />} />
            <Route path="/flooring-selector" element={<GuidedSelectorPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/:slug" element={<ArticlePage />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/concierge" element={<ConciergePage />} />
            <Route path="/maintenance" element={<MaintenanceProgramPage />} />
            
            {/* Custom Solutions Hub */}
            <Route path="/custom" element={<CustomHubPage />} />
            <Route path="/custom/logo-mats" element={<CustomLogoMatsPage />} />
            <Route path="/custom/flooring-solutions" element={<CustomFlooringSolutionsPage />} />
            <Route path="/custom/installations" element={<CustomInstallationsPage />} />
            <Route path="/custom/consultation" element={<CustomConsultationPage />} />
            <Route path="/custom/portfolio" element={<CustomPortfolioPage />} />
            <Route path="/custom/portfolio/:slug" element={<CaseStudyPage />} />
            
            {/* Careers */}
            <Route path="/careers" element={<CareersPage />} />
            
            {/* Legal pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            
            {/* 404 — first consult the legacy redirect map. If no match,
                render the actual NotFoundPage. */}
            <Route path="*" element={<LegacyRedirectsGate fallback={<NotFoundPage />} />} />
          </Routes>
        </DashboardAuthProvider>
      </BrowserRouter>
      <Toaster />
    </HelmetProvider>
  );
}

export default App;
